/**
 * Animated accordion for <details data-accordion>.
 *
 * Expected structure:
 *   <details data-accordion>
 *     <summary>...</summary>
 *     <div data-accordion-content>...content...</div>
 *   </details>
 *
 * Optional: wrap a group in <… data-single-open> to make opening one
 * automatically close the others in that group.
 *
 * Uses Web Animations API on the <details> element's height. Native
 * <details> hides non-summary content with no transition, so we
 * preventDefault on summary clicks and drive open/close ourselves.
 */
(() => {
  if (window.__accordionInit) return;
  window.__accordionInit = true;

  const DURATION = 300;
  const EASING = 'ease-in-out';

  const ICON_DURATION = 500;
  const ICON_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
  const ARM_DURATION = 300;
  const ARM_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

  class Accordion {
    constructor(detailsEl) {
      this.details = detailsEl;
      this.summary = detailsEl.querySelector('summary');
      this.content = detailsEl.querySelector('[data-accordion-content]');
      if (!this.summary || !this.content) return;

      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;
      detailsEl.__accordion = this;
      this.summary.addEventListener('click', (e) => this.onClick(e));
    }

    onClick(e) {
      e.preventDefault();
      const willOpen = !this.details.open || this.isClosing;

      // Single-open behavior: close sibling accordions within the same group
      if (willOpen) {
        const group = this.details.closest('[data-single-open]');
        if (group) {
          group.querySelectorAll('details[data-accordion]').forEach((d) => {
            if (d === this.details) return;
            if (d.__accordion && (d.open || d.__accordion.isExpanding)) {
              d.__accordion.shrink();
            }
          });
        }
      }

      this.details.style.overflow = 'hidden';
      if (this.isClosing || !this.details.open) {
        this.openAnimated();
      } else {
        this.shrink();
      }
    }

    playIconAnim(key, el, keyframes, duration, easing, finalTransform) {
      this.iconAnims = this.iconAnims || {};
      if (this.iconAnims[key]) {
        try { this.iconAnims[key].cancel(); } catch (e) {}
      }
      const anim = el.animate(keyframes, { duration, easing, fill: 'forwards' });
      anim.onfinish = () => {
        el.style.transform = finalTransform;
        try { anim.cancel(); } catch (e) {}
      };
      this.iconAnims[key] = anim;
    }

    animateIcon(open) {
      const icon = this.details.querySelector('.faq-item__icon-core');
      const arm = this.details.querySelector('.faq-item__icon-bar--v');

      if (icon) {
        const from = open ? 0 : 180;
        const to = open ? 180 : 0;
        const mid = (from + to) / 2;
        this.playIconAnim(
          'icon',
          icon,
          [
            { transform: `rotate(${from}deg) scale(1)` },
            { transform: `rotate(${mid}deg) scale(1.18)`, offset: 0.5 },
            { transform: `rotate(${to}deg) scale(1)` },
          ],
          ICON_DURATION,
          ICON_EASING,
          `rotate(${to}deg) scale(1)`
        );
      }

      if (arm) {
        const from = open ? 'scaleY(1)' : 'scaleY(0)';
        const to = open ? 'scaleY(0)' : 'scaleY(1)';
        this.playIconAnim('arm', arm, [{ transform: from }, { transform: to }], ARM_DURATION, ARM_EASING, to);
      }
    }

    shrink() {
      this.isClosing = true;
      this.details.style.overflow = 'hidden';
      const startHeight = `${this.details.offsetHeight}px`;
      const endHeight = `${this.summary.offsetHeight}px`;

      if (this.animation) this.animation.cancel();

      this.animateIcon(false);
      this.animation = this.details.animate(
        { height: [startHeight, endHeight] },
        { duration: DURATION, easing: EASING }
      );
      this.animation.onfinish = () => this.onAnimationFinish(false);
      this.animation.oncancel = () => { this.isClosing = false; };
    }

    openAnimated() {
      this.details.style.height = `${this.details.offsetHeight}px`;
      this.details.open = true;
      this.animateIcon(true);
      window.requestAnimationFrame(() => this.expand());
    }

    expand() {
      this.isExpanding = true;
      const startHeight = `${this.details.offsetHeight}px`;
      const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;

      if (this.animation) this.animation.cancel();

      this.animation = this.details.animate(
        { height: [startHeight, endHeight] },
        { duration: DURATION, easing: EASING }
      );
      this.animation.onfinish = () => this.onAnimationFinish(true);
      this.animation.oncancel = () => { this.isExpanding = false; };
    }

    onAnimationFinish(open) {
      this.details.open = open;
      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;
      this.details.style.height = '';
      this.details.style.overflow = '';
    }
  }

  function init(root) {
    (root || document).querySelectorAll('details[data-accordion]:not([data-accordion-ready])').forEach((el) => {
      el.setAttribute('data-accordion-ready', '');
      new Accordion(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Re-bind on Shopify section reloads in the theme editor
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
